/**
 * journey-offers.js - the campaign's prices, read from the Business App.
 *
 * BRS amendment 2026-09-17, block J2: *"Changing the R799 or R999 setting changes
 * the dashboard tip, the buy link price and the theme message without a deploy."*
 * A price written into Liquid fails that test, so this theme holds NO price. It
 * fetches the whole sentence, already worded and already priced, from the app's
 * public offers endpoint and drops it into a slot.
 *
 * THE CONTRACT (Business App branch feat/m04-journey-offer-settings, 8517c0e7,
 * reported by that lane on 2026-09-18; UNMERGED and UNDEPLOYED at the time of
 * writing, so this is coded to the documented shape, not to a live response):
 *   GET https://www.glowming.business/api/offers, no auth, Cache-Control public
 *   max-age=60. CORS allows exactly glowming.co.za, www.glowming.co.za and
 *   glowmingsa.myshopify.com (the last one so a theme PREVIEW can read it too;
 *   BA 0ea48327, reported 2026-09-18). No other origin can read the answer, so
 *   a copy of this page on another domain shows no price rather than a wrong one.
 *   200: { status: "ok", generatedAt, offers: [{ key, label, kind, amount,
 *          currency, pouches, audience, appliesToSubscriptions }],
 *          messages: { freeDelivery, journeySpecial, hostSpecial } }
 *   503: { status: "unavailable", reason } - no offers key, no messages key.
 *   Amounts are rand, VAT-inclusive. Only RUNNING offers are listed; every
 *   campaign offer starts 2026-09-25, so before launch a correct answer is an
 *   empty list with null messages. On failure the app answers 503 with the same
 *   empty shape.
 *
 * SO: a null or missing message renders NOTHING. Never a stale price, never a
 * sentence with a gap in it, and never a price this theme made up.
 *
 * The visitor-facing wording is the Business App's (Heyu's words in the
 * amendment). The only sentences this file can show are the ones it is handed.
 */
(function () {
  'use strict';

  if (typeof window !== 'undefined' && window.GlowmingOffers) return;

  /** Matches the endpoint's own max-age, so the theme is never fresher than the app promises. */
  var TTL_MS = 60 * 1000;
  var CACHE_KEY = 'glowming_offers_v1';
  /** The affiliate tracker's own key and cookie: same names, read-only. */
  var AFFILIATE_KEY = 'glowming_affiliate_ref';

  /**
   * Is this visitor linked to an affiliate? They see their host block instead of
   * the free-delivery line (J2b). Any one signal is enough, and an unreadable
   * store is simply no signal.
   *
   * @param {object} sources - {storage, cookie, cartRef}
   * @returns {boolean}
   */
  function isAffiliateLinked(sources) {
    var source = sources || {};
    if (source.cartRef) return true;
    var cookie = source.cookie || '';
    if (cookie.split('; ').some(function (part) { return part.indexOf(AFFILIATE_KEY + '=') === 0 && part.length > AFFILIATE_KEY.length + 1; })) return true;
    try {
      var stored = source.storage && source.storage.getItem(AFFILIATE_KEY);
      return typeof stored === 'string' && stored !== '';
    } catch (err) {
      return false;
    }
  }

  /** The sentence for a slot, or null when the app has none for it. */
  function messageFor(payload, slot) {
    var messages = payload && payload.messages;
    if (!messages) return null;
    var field = slot === 'free_delivery' ? 'freeDelivery' : slot === 'host_special' ? 'hostSpecial' : 'journeySpecial';
    var value = messages[field];
    return typeof value === 'string' && value.trim() !== '' ? value : null;
  }

  /** One running offer by KEY (never by label, which an admin can rename). */
  function offerFor(payload, key) {
    var offers = payload && payload.offers;
    if (!Array.isArray(offers)) return null;
    return offers.filter(function (offer) { return offer && offer.key === key; })[0] || null;
  }

  /** A cached payload that is still within its TTL, else null. */
  function readCache(storage, now) {
    try {
      var raw = storage && storage.getItem(CACHE_KEY);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || typeof entry.at !== 'number' || now - entry.at > TTL_MS || now < entry.at) return null;
      return entry.payload || null;
    } catch (err) {
      return null;
    }
  }

  function writeCache(storage, now, payload) {
    try {
      storage.setItem(CACHE_KEY, JSON.stringify({ at: now, payload: payload }));
    } catch (err) {
      /* A full or blocked store costs a fetch, never a wrong price. */
    }
  }

  /**
   * Fetch the offers, or null when they cannot be had. A 503 (the app's own
   * failure shape) is treated as "no offers", which renders nothing.
   *
   * @param {string} endpoint
   * @returns {Promise<object|null>}
   */
  function fetchOffers(endpoint) {
    return fetch(endpoint, { mode: 'cors', credentials: 'omit', headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (payload) {
        /* `status` is the app's own word for which answer this is: "ok" carries
           offers and messages (both may be empty before 25 September), while
           "unavailable" carries NEITHER key. Reading messages off a failure body
           would throw, and caching one would hold the storefront blank for a
           minute after the app recovers, so only an "ok" answer counts. */
        if (!payload || typeof payload !== 'object' || payload.status !== 'ok') return null;
        return payload.messages && typeof payload.messages === 'object' ? payload : null;
      })
      .catch(function () { return null; });
  }

  /** Fill one slot element, or leave it empty and hidden. */
  function fillSlot(element, payload, linked) {
    var slot = element.dataset.journeyOffer;
    if (slot === 'free_delivery' && linked) return;
    var message = messageFor(payload, slot);
    if (!message) return;
    element.textContent = message;
    element.hidden = false;
  }

  function start() {
    var slots = Array.prototype.slice.call(document.querySelectorAll('[data-journey-offer]'));
    if (slots.length === 0) return;
    var endpoint = slots[0].dataset.offersEndpoint;
    if (!endpoint) return;

    var linked = isAffiliateLinked({
      storage: window.localStorage,
      cookie: document.cookie,
      cartRef: slots[0].dataset.cartRef,
    });

    var now = Date.now();
    var cached = readCache(window.sessionStorage, now);
    var settle = function (payload) {
      if (!payload) return;
      slots.forEach(function (element) { fillSlot(element, payload, linked); });
      document.dispatchEvent(new CustomEvent('glowming:offers:loaded', { detail: payload }));
    };

    if (cached) {
      settle(cached);
      return;
    }
    fetchOffers(endpoint).then(function (payload) {
      if (payload) writeCache(window.sessionStorage, Date.now(), payload);
      settle(payload);
    });
  }

  window.GlowmingOffers = {
    TTL_MS: TTL_MS,
    CACHE_KEY: CACHE_KEY,
    AFFILIATE_KEY: AFFILIATE_KEY,
    isAffiliateLinked: isAffiliateLinked,
    messageFor: messageFor,
    offerFor: offerFor,
    readCache: readCache,
    writeCache: writeCache,
    fetchOffers: fetchOffers,
    fillSlot: fillSlot,
  };

  if (typeof document !== 'undefined' && document.querySelectorAll) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
    /* The cart section is redrawn in place after each change
       (assets/cart-section-refresh.js), bringing empty slots; refill them. The
       sessionStorage cache makes this a local read, not a request. */
    document.addEventListener('glowming:cart:rendered', start);
  }
})();
